import React, { useRef, useEffect, useCallback, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { usePhotoUploadQueue } from "@/hooks/usePhotoUploadQueue";
import { useNativeCrop } from "@/hooks/useNativeCrop";
import { NativeCropModal } from "@/components/crop/NativeCropModal";
import PhotoUploadButton from "./PhotoUploadButton";
import PhotoUploadProgress from "./PhotoUploadProgress";
import { debugLogger } from "@/utils/debugLogger";

interface OptimizedPhotoUploaderProps {
  onPhotosUpdate: (newPhotos: string[]) => void;
  existingPhotos?: string[];
  registerCleanup?: (cleanup: () => void) => void;
  onActiveWorkChange?: (hasWork: boolean) => void;
  hideButton?: boolean; // If true, button is not rendered internally
  onButtonPropsChange?: (props: { onClick: () => void; disabled: boolean }) => void; // Callback with button props for external rendering
}

const OptimizedPhotoUploader: React.FC<OptimizedPhotoUploaderProps> = ({ 
  onPhotosUpdate, 
  existingPhotos = [],
  registerCleanup,
  onActiveWorkChange,
  hideButton = false,
  onButtonPropsChange
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { user } = useAuth();
  
  const {
    enqueueCropping,
    onCropComplete: queueCropComplete,
    skipCurrentCrop,
    getCurrentCropFile,
    croppingQueue,
    uploadQueue,
    failedUploads,
    currentCroppingIndex,
    currentUploadIndex,
    totalCropping,
    totalUploading,
    totalFailed,
    isUploading,
    hasActiveWork,
    error,
    retryFailedUpload,
    discardFailedUpload,
  } = usePhotoUploadQueue({
    userId: user?.id || "",
    existingPhotos,
    onPhotosChange: onPhotosUpdate,
    maxPhotos: 6,
  });

  // Debounce button to prevent multiple rapid clicks
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);

  const {
    isOpen: isCropModalOpen,
    imageSrc: cropImageSrc,
    openCrop,
    closeCrop: closeCropModal,
    handleCropComplete: onCropComplete,
  } = useNativeCrop({
    onCropComplete: queueCropComplete,
  });

  // Auto-open crop modal when there's a file in cropping queue
  useEffect(() => {
    const currentFile = getCurrentCropFile();
    if (currentFile && !isCropModalOpen) {
      openCrop(currentFile);
    }
  }, [croppingQueue.length, getCurrentCropFile, openCrop, isCropModalOpen]);

  // Notify parent about active work status
  useEffect(() => {
    if (onActiveWorkChange) {
      onActiveWorkChange(hasActiveWork);
    }
  }, [hasActiveWork, onActiveWorkChange]);

  // Register cleanup callback with parent
  useEffect(() => {
    if (registerCleanup) {
      registerCleanup(() => {
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      });
    }
  }, [registerCleanup]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    };
  }, []);

  // Handle cancel: skip current photo and close modal
  const handleCancelCrop = useCallback(() => {
    skipCurrentCrop();
    closeCropModal();
  }, [skipCurrentCrop, closeCropModal]);

  const isImageFile = (file: File): boolean => {
    const validMimeTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validMimeTypes.includes(file.type.toLowerCase())) {
      return false;
    }
    
    const validExtensions = ['.jpg', '.jpeg', '.png', '.webp'];
    const fileName = file.name.toLowerCase();
    return validExtensions.some(ext => fileName.endsWith(ext));
  };

  const handleAddPhoto = useCallback(() => {
    if (isButtonDisabled || hasActiveWork) return;
    
    // Clear any lingering modal state
    if (isCropModalOpen) {
      closeCropModal();
    }
    
    // Debounce: disable button for 1 second
    setIsButtonDisabled(true);
    setTimeout(() => setIsButtonDisabled(false), 1000);
    
    fileInputRef.current?.click();
  }, [isButtonDisabled, hasActiveWork, isCropModalOpen, closeCropModal]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;

    const allFiles = Array.from(e.target.files);
    const imageFiles = allFiles.filter(isImageFile);
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    if (imageFiles.length === 0) return;

    // Add all files to cropping queue (with duplicate prevention)
    enqueueCropping(imageFiles);
  };

  // Notify parent about button props changes (for external rendering)
  useEffect(() => {
    if (onButtonPropsChange) {
      onButtonPropsChange({
        onClick: handleAddPhoto,
        disabled: isButtonDisabled || hasActiveWork
      });
    }
  }, [isButtonDisabled, hasActiveWork, onButtonPropsChange]);

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp"
        multiple
        onChange={handleFileChange}
        className="hidden"
      />

      {!hideButton && (
        <PhotoUploadButton
          onClick={handleAddPhoto}
          disabled={isButtonDisabled || hasActiveWork}
        />
      )}

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mt-4">
          <p className="text-red-200 text-sm">{error}</p>
        </div>
      )}

      {/* Progress display with failed uploads */}
      <PhotoUploadProgress
        croppingProgress={{ current: currentCroppingIndex, total: totalCropping }}
        uploadProgress={{ current: currentUploadIndex, total: totalUploading }}
        failedUploads={failedUploads}
        onRetryUpload={retryFailedUpload}
        onDiscardUpload={discardFailedUpload}
      />

      <NativeCropModal
        isOpen={isCropModalOpen}
        imageSrc={cropImageSrc}
        onCropComplete={onCropComplete}
        onClose={handleCancelCrop}
      />
    </>
  );
};

export default OptimizedPhotoUploader;
